import { useEffect, useId, useMemo } from "react"

import { Line } from "react-chartjs-2"
import { useSelector } from "react-redux"

import EmptyComponent from "../../../Components/Empty"
import { useIncognito } from "../../../hooks/useIncognito"
import usePrefersReducedMotion from "../../../hooks/usePrefersReducedMotion"
import { buildKpiComparisonOverlay } from "../../DashboardPage/components/chartsOptions"
import { tooltipLineWithPoints } from "../../DashboardPage/components/chartsPlagins"
import { formatCount } from "../utils/formats"
import { yAxisRange } from "./axis"
import { cardDateLabel, intervalChartOptions, SURFACE_COLOR, tooltipDateLabel } from "./chartBase"
import { externalTooltip } from "./chartTooltip"
import { afterRevealAnimations, firstRender, lastValueLabels } from "./plugins"

import styles from "./charts.module.less"

const NO_COMPARISON = []
const NO_BARS = []

const hasData = (line) => line.data.some((value) => value)

// A series that is zero in every interval is left out, with its legend entry (DAA-2-25) — unless
// that would leave the chart empty: a period without data still draws its first line, flat at 0.
const visibleSeries = (series, hasBars) => {
  const withData = series.filter(hasData)

  return withData.length || hasBars ? withData : series.slice(0, 1)
}

// Room right of the plot for the widest last-value label (12px text on an 8px-padded pill, 8px out).
const lastLabelRoom = (lines, yFormat) => {
  const longest = Math.max(
    0,
    ...lines.map((line) => String(yFormat(line.data[line.data.length - 1])).length)
  )

  return Math.max(52, longest * 7 + 24)
}

// The page's line chart (DAA-2-23 … DAA-2-25): straight 2px segments, the segment to the last point
// dashed while that interval is incomplete, the last value of each current-period line labelled, a
// dashed comparison line per series when comparing (the platform's KPI comparison overlay), and the
// platform's dashed guide with a ring on every line on hover. `series` is
// [{ key, label, color, data }], `comparison` the same shape plus each point's `dates` (a comparison
// line whose series was left out is left out too) or null. `bars` are drawn as bars behind the lines, on the same axis
// (the Active merchants trend's New, DAA-4-10); an all-zero one is left out (DAA-2-25). `fill` draws
// the area under the series; `zoomY` fits the y-axis to the data, `yMax` pins its top. `yTickFormat`
// writes the y-axis ticks when they read differently from the values (a rate's "85%" against
// "85.3%"; DAA-5-05), `yMinStep` keeps its zoomed step from going below what they can show, and
// `yWhole` steps the axis in whole units (counts, whole dollars). `legend` defaults to "more than
// one current-period series". `tooltipColumns` draws the tooltip as the platform's comparison card
// (prevent/analytics): the first series' date and Total, and beside it the first comparison line's
// (CHA1-4525).
const TrendLineChart = ({
  labels,
  series,
  comparison = null,
  bars = NO_BARS,
  lastIntervalIncomplete = false,
  yFormat = formatCount,
  yWhole = yFormat === formatCount,
  yTickFormat = yFormat,
  yMinStep,
  zoomY = false,
  yMax,
  fill = false,
  legend,
  height = 260,
  tooltipId,
  tooltipColumns = false
}) => {
  const theme = useSelector((state) => state.nav.theme)
  const isMobile = useSelector((state) => state.nav.isMobile)
  const incognito = useIncognito()
  const prefersReducedMotion = usePrefersReducedMotion()
  const generatedId = useId()
  const elementId = tooltipId ?? `admin-deflect-tooltip-${generatedId}`

  // The tooltip element lives on <body>, outside React, so the chart that creates it removes it.
  useEffect(() => () => document.getElementById(elementId)?.remove(), [elementId])

  const barSeries = useMemo(() => bars.filter(hasData), [bars])
  const lines = useMemo(() => visibleSeries(series, barSeries.length > 0), [series, barSeries])
  // A comparison line is drawn whenever comparing, zero or not, as on the platform's overview charts.
  const comparisonLines = useMemo(() => {
    const shownKeys = new Set(lines.map((line) => line.key))

    return (comparison ?? NO_COMPARISON).filter((line) => !line.key || shownKeys.has(line.key))
  }, [comparison, lines])
  const lastIndex = labels.length - 1
  const showLegend = legend ?? lines.length > 1

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        ...lines.map((line) => ({
          label: line.label,
          data: line.data,
          borderColor: line.color,
          // The area fill itself is firstRender's, which fades it in after the line (DAA-1-20).
          backgroundColor: fill ? `${line.color}00` : line.color,
          areaColor: fill ? line.color : undefined,
          pointBackgroundColor: line.color,
          fill: fill ? "origin" : false,
          borderWidth: 2,
          borderCapStyle: "round",
          borderJoinStyle: "round",
          pointRadius: lastIndex === 0 ? 4 : 0,
          pointHoverRadius: 0,
          // Room past the plot for that point, which sits on the axis when its value is 0.
          clip: 6,
          labelLast: true,
          segment: {
            borderDash: (context) =>
              lastIntervalIncomplete && context.p1DataIndex === lastIndex ? [5, 5] : undefined
          }
        })),
        ...comparisonLines.flatMap((line) =>
          buildKpiComparisonOverlay({ labels, series: line.data, label: line.label }).map(
            (overlay) => ({
              ...overlay,
              isComparison: true,
              backgroundColor: overlay.borderColor,
              // A one-interval period ("Today") has no segment to draw, so its point shows instead.
              pointRadius: lastIndex === 0 ? 4 : 0
            })
          )
        ),
        // A higher `order` is drawn first, so the bars stay behind the lines.
        ...barSeries.map((bar) => ({
          type: "bar",
          label: bar.label,
          data: bar.data,
          backgroundColor: bar.color,
          hoverBackgroundColor: bar.color,
          // Not drawn (no border), but the tooltip's marker reads it.
          borderColor: bar.color,
          borderWidth: 0,
          categoryPercentage: 1,
          barPercentage: 0.45,
          // Narrow enough that the last line value, labelled 8px right of its point, clears its bar.
          maxBarThickness: 16,
          order: 1
        }))
      ]
    }),
    [labels, lines, comparisonLines, barSeries, fill, lastIndex, lastIntervalIncomplete]
  )

  const options = useMemo(() => {
    const allValues = [...lines, ...comparisonLines, ...barSeries].flatMap((line) => line.data)
    const surface = SURFACE_COLOR[theme] ?? SURFACE_COLOR.light
    // The tooltip's content: with `tooltipColumns` the comparison card's columns, stacked on a phone
    // as the platform's; else rows of the series the legend has not hidden, the comparisons muted
    // under a hairline, as in the platform's overview tooltip.
    const rowsFor = (dataIndex, chart) => {
      if (tooltipColumns) {
        const columns = [
          {
            header: cardDateLabel(labels[dataIndex]),
            total: { label: "Total", value: yFormat(lines[0]?.data[dataIndex]) }
          }
        ]
        const compared = comparisonLines[0]
        const comparedDate = compared?.dates?.[dataIndex]

        // A point without a counterpart day (past a shorter comparison period) shows the current
        // period alone, as on the platform's charts.
        if (comparedDate) {
          columns.push({
            header: cardDateLabel(comparedDate),
            total: { label: "Total", value: yFormat(compared.data[dataIndex]) }
          })
        }

        return { columns, stacked: isMobile }
      }

      const row = (dataset) => ({
        marker: { color: dataset.borderColor, dashed: dataset.isComparison },
        label: dataset.label,
        value: yFormat(dataset.data[dataIndex])
      })
      const shown = chart.data.datasets.filter((_dataset, index) => chart.isDatasetVisible(index))

      return {
        header: tooltipDateLabel(labels[dataIndex]),
        rows: shown.filter((dataset) => !dataset.isComparison).map(row),
        comparisonRows: shown.filter((dataset) => dataset.isComparison).map(row)
      }
    }

    const chartOptions = intervalChartOptions({
      theme,
      labels,
      range: yAxisRange(allValues, {
        zoom: zoomY,
        max: yMax,
        whole: yWhole,
        minStep: yMinStep
      }),
      yFormat: yTickFormat,
      legend: showLegend,
      // Comparison lines are not legend entries (DAA-3-04).
      legendFilter: (item, chartData) => !chartData.datasets[item.datasetIndex].isComparison,
      tooltip: externalTooltip({
        tooltipId: elementId,
        rowsFor,
        offset: lines.length > 1 ? 16 : 20
      })
    })

    return {
      ...chartOptions,
      ...(prefersReducedMotion ? { animation: false } : { animations: afterRevealAnimations }),
      // Room for the last-value labels at the right end.
      layout: { padding: { right: lastLabelRoom(lines, yFormat), top: 10 } },
      // Half an interval at each end, so the first and the last bar are not cut at the plot's edges.
      scales: {
        ...chartOptions.scales,
        x: { ...chartOptions.scales.x, offset: barSeries.length > 0 }
      },
      plugins: {
        ...chartOptions.plugins,
        "vertical-line-plugin-with-points": { theme },
        "admin-last-value-labels": { format: yFormat, surface }
      }
    }
  }, [
    theme,
    labels,
    lines,
    comparisonLines,
    barSeries,
    yFormat,
    yWhole,
    yTickFormat,
    yMinStep,
    zoomY,
    yMax,
    showLegend,
    elementId,
    prefersReducedMotion,
    tooltipColumns,
    isMobile
  ])

  // As tall as the chart, so the card keeps its height when a filter empties it.
  return (
    <div className={styles.chart} style={{ height }}>
      {labels.length && (lines.length || barSeries.length) ? (
        <Line
          key={`${theme}-${incognito}`}
          data={data}
          options={options}
          plugins={[firstRender, lastValueLabels, tooltipLineWithPoints]}
        />
      ) : (
        <div className={styles.empty}>
          <EmptyComponent type="no_data_available" style={{ minHeight: height }} />
        </div>
      )}
    </div>
  )
}

export default TrendLineChart
